import { ReactNode, useCallback, useEffect, useState } from 'react';
import { CsbOption, CsbOptionProps, SELECT_BEHAVIOUR } from './custom-select-box-option';
import { useStyles } from './custom-select-box.style';

type CustomSelectBoxProps = {
    /**
     * Option to pass ref to component
     */
    forwardedRef?: any;
    /**
     * Options to be pass from the view.
     */
    options?: CsbOptionProps[];
    /**
     * Callback function that fire when radio button is selected.
     * This Provides value and selected radio button index to the view
     */
    onChange: (value: string, index: number) => void;
    /**
     * Name of the radio button. Provide meaningful value since this is important for sas-ci360
     */
    name: string;
    /**
     * This will switch between radio button and check box
     */
    behaviour: SELECT_BEHAVIOUR;
    /**
     * This helps to sync with your parent form validation
     */
    isSubmitted?: boolean;
    /**
     * This will show additional label at the bottom with 'VALGT'
     */
    showSelected?: boolean;
    /**
     * Options to be rendered
     */
    children?: ReactNode | ReactNode[];
};

const CustomSelectBox = ({
    options,
    onChange,
    name,
    behaviour,
    isSubmitted = false,
    showSelected = false,
    forwardedRef = null,
    children,
    ...rest
}: CustomSelectBoxProps): JSX.Element => {
    const [content, setContent] = useState(<></>);
    const [radioOptions, setRadioOptions] = useState<CsbOptionProps[]>([]);
    const classes = useStyles();

    useEffect(() => {
        if (children) {
            const opt = Array.isArray(children)
                ? children.map((item: ReactNode) => ({ ...(item as any).props }))
                : children.hasOwnProperty('props')
                ? [{ ...(children as any)?.props, checked: (children as any)?.props.checked }]
                : [];
            setRadioOptions(opt);
        } else if (options) {
            setRadioOptions(options);
        }
    }, [children, options]);

    const onChangeHandler = useCallback(
        (value: string, i: number) => {
            setRadioOptions(
                radioOptions.map((item, index: number) => {
                    if (behaviour === SELECT_BEHAVIOUR.RADIO) {
                        item.checked = index === i;
                    } else if (index === i) {
                        item.checked = !item.checked;
                    }
                    return item;
                })
            );
            onChange(value, i);
        },
        [onChange, radioOptions, behaviour]
    );

    // Generate html according to the relevant prop's value
    const createNumComponents = useCallback(
        (item: CsbOptionProps, index: number) => {
            return (
                <CsbOption
                    key={index}
                    label={item.label}
                    value={item.value}
                    icon={item.icon}
                    iconSize={item.iconSize}
                    iconLocation={item.iconLocation}
                    checked={item.checked}
                    disabled={item.disabled}
                    index={index}
                    name={name}
                    showSelected={showSelected}
                    isSubmitted={isSubmitted}
                    onChangeHandler={onChangeHandler}
                    behaviour={behaviour}
                    {...rest}
                />
            );
        },
        [name, showSelected, isSubmitted, onChangeHandler, behaviour]
    );

    // Check radio group have minimum of two values
    const generateElement = useCallback(
        (numEl: number): any => {
            if (numEl > 0) {
                return radioOptions.map((item: CsbOptionProps, index: number) => {
                    item.checked = item.checked || false;
                    return createNumComponents(item, index);
                });
            }
            return <div className={classes.radioButtonLabel}>Invalid options length</div>;
        },
        [classes.radioButtonLabel, createNumComponents, radioOptions, behaviour]
    );

    useEffect(() => {
        const numOfElements = radioOptions?.length;
        if (numOfElements) {
            setContent(generateElement(numOfElements));
        }
    }, [generateElement, radioOptions]);

    return (
        <div ref={forwardedRef} className={`${classes.container} MuiGrid-root`}>
            {content}
        </div>
    );
};

export { CustomSelectBox };
export type { CustomSelectBoxProps };
export { SELECT_BEHAVIOUR };
export type { CsbOptionProps };
export { CsbOption };
